import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sparkles, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

function Models() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", provider: "openai", model_id: "", cost_per_1k_tokens: 0, enabled: true, description: "" });

  const load = async () => { const { data } = await api.get("/admin/ai/models"); setItems(data); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/admin/ai/models", form); toast.success("Model added"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (m) => { await api.put(`/admin/ai/models/${m.id}`, { ...m, enabled: !m.enabled }); load(); };
  const del = async (m) => { if (window.confirm(`Remove ${m.name}?`)) { await api.delete(`/admin/ai/models/${m.id}`); load(); } };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => setOpen(true)} data-testid="ai-add-model"><Plus className="w-4 h-4 mr-1" /> Register model</Button></div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Provider</TableHead><TableHead>Model id</TableHead><TableHead>₹ / 1k tok</TableHead><TableHead>Enabled</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((m) => (
              <TableRow key={m.id} data-testid={`ai-model-${m.id}`}>
                <TableCell className="font-semibold">{m.name}</TableCell>
                <TableCell><Badge variant="outline">{m.provider}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{m.model_id}</TableCell>
                <TableCell>{m.cost_per_1k_tokens}</TableCell>
                <TableCell><Switch checked={!!m.enabled} onCheckedChange={() => toggle(m)} /></TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => del(m)}><Trash2 className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No models registered. Click <em>Register model</em> to start.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Register AI model</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ai-model-name" /></div>
            <div><Label>Provider</Label><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></div>
            <div><Label>Model id</Label><Input value={form.model_id} onChange={(e) => setForm({ ...form, model_id: e.target.value })} placeholder="e.g. gpt-5.2 or claude-sonnet-4.6" /></div>
            <div><Label>Cost per 1k tokens (₹)</Label><Input type="number" value={form.cost_per_1k_tokens} onChange={(e) => setForm({ ...form, cost_per_1k_tokens: parseFloat(e.target.value || "0") })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="ai-save-model">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Prompts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", module: "procurement", template: "", description: "" });
  const load = async () => { const { data } = await api.get("/admin/ai/prompts"); setItems(data); };
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.post("/admin/ai/prompts", form); setOpen(false); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const del = async (p) => { await api.delete(`/admin/ai/prompts/${p.id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => setOpen(true)} data-testid="ai-add-prompt"><Plus className="w-4 h-4 mr-1" /> New prompt</Button></div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((p) => (
          <Card key={p.id} data-testid={`ai-prompt-${p.id}`}>
            <CardContent className="p-4 space-y-1">
              <div className="flex items-center justify-between"><div className="font-semibold">{p.title}</div><Badge variant="outline">{p.module}</Badge></div>
              <div className="text-xs text-muted-foreground">{p.description}</div>
              <pre className="text-[11px] bg-muted/40 p-2 rounded mt-2 overflow-x-auto">{p.template}</pre>
              <div className="text-right"><Button size="sm" variant="ghost" onClick={() => del(p)}><Trash2 className="w-4 h-4" /></Button></div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="text-center text-muted-foreground py-10 col-span-2">No prompts yet — add module templates for Procurement, Inventory, Finance, Sales and Manufacturing assistants.</div>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New prompt template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Module</Label><Input value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })} placeholder="procurement / inventory / finance / sales / mfg" /></div>
            <div><Label>Template</Label><textarea className="w-full border rounded p-2 text-sm" rows="6" value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })} /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Credits() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/ai/credits").then(({ data }) => setItems(data)); }, []);
  return (
    <div className="surface-card p-4 overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Tenant</TableHead><TableHead>Balance</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((c) => <TableRow key={c.id}><TableCell className="font-mono text-xs">{c.tenant_id}</TableCell><TableCell>{c.balance.toLocaleString()}</TableCell></TableRow>)}
          {items.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-10">No credits allocated yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function Usage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/ai/usage").then(({ data }) => setItems(data)); }, []);
  return (
    <div className="surface-card p-4 overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Tenant</TableHead><TableHead>Module</TableHead><TableHead>Tokens</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((u) => (
            <TableRow key={u.id}>
              <TableCell className="font-mono text-xs">{new Date(u.created_at).toLocaleString()}</TableCell>
              <TableCell className="font-mono text-xs">{u.tenant_id?.slice(0,8)}</TableCell>
              <TableCell>{u.module}</TableCell><TableCell>{u.tokens}</TableCell>
              <TableCell><Badge variant="outline">{u.status || "OK"}</Badge></TableCell>
            </TableRow>
          ))}
          {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No AI usage logged yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

export default function AICenter() {
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-ai-center">
      <div className="flex items-center gap-3">
        <Sparkles className="w-6 h-6 text-violet-600" />
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">AI platform center</h1>
          <p className="text-muted-foreground">Configure models, prompt library, credits, usage analytics and performance.</p>
        </div>
      </div>
      <Tabs defaultValue="models">
        <TabsList>
          <TabsTrigger value="models" data-testid="ai-tab-models">Models</TabsTrigger>
          <TabsTrigger value="prompts" data-testid="ai-tab-prompts">Prompts</TabsTrigger>
          <TabsTrigger value="credits" data-testid="ai-tab-credits">Credits</TabsTrigger>
          <TabsTrigger value="usage" data-testid="ai-tab-usage">Usage</TabsTrigger>
        </TabsList>
        <TabsContent value="models"><Models /></TabsContent>
        <TabsContent value="prompts"><Prompts /></TabsContent>
        <TabsContent value="credits"><Credits /></TabsContent>
        <TabsContent value="usage"><Usage /></TabsContent>
      </Tabs>
    </div>
  );
}
